import React, { Suspense } from 'react';
import { Refine, Authenticated } from '@refinedev/core';
import { RefineThemes, useNotificationProvider } from '@refinedev/antd';
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import deDE from 'antd/locale/de_DE';
import '@refinedev/antd/dist/reset.css';
import '@/shared/styles/global.css';
import 'dayjs/locale/de';
import dayjs from 'dayjs';

import routerProvider, { DocumentTitleHandler, UnsavedChangesNotifier } from '@refinedev/react-router-v6';
import { authProvider, isSuperAdmin } from '@/auth/authProvider';
import { impersonationStorage, isImpersonationValid } from '@/auth/session';
import { dataProviderInstance } from '@/shared/dataProvider';

// ── Auth ─────────────────────────────────────────────
import { LoginPage } from '@/auth/LoginPage';
import { ForgotPasswordPage } from '@/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/auth/ResetPasswordPage';

// ── Admin ─────────────────────────────────────────────
import { AdminLayout } from '@/admin/components/AdminLayout';

// ── Customer ──────────────────────────────────────────
import { CustomerLayout } from '@/customer/components/CustomerLayout';

// ── Ant Design Icons ──────────────────────────────────
import { TeamOutlined, MessageOutlined, BellOutlined, KeyOutlined, SettingOutlined, DatabaseOutlined, BarChartOutlined, CreditCardOutlined, DollarOutlined, ApiOutlined, WalletOutlined, HddOutlined, HeartOutlined } from '@ant-design/icons';

// ── Lazy-loaded Pages (React.lazy + Suspense) ────────
// Admin
const AdminDashboard = React.lazy(() => import('@/admin/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const TenantsPage = React.lazy(() => import('@/admin/pages/TenantsPage').then((m) => ({ default: m.TenantsPage })));
const TenantDetail = React.lazy(() => import('@/admin/pages/TenantDetail').then((m) => ({ default: m.TenantDetail })));
const AdminSessionsPage = React.lazy(() => import('@/admin/pages/SessionsPage').then((m) => ({ default: m.SessionsPage })));
const AdminSessionReplay = React.lazy(() => import('@/admin/pages/SessionReplay').then((m) => ({ default: m.SessionReplay })));
const AlertsPage = React.lazy(() => import('@/admin/pages/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const AnalyticsPage = React.lazy(() => import('@/admin/pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const BillingPage = React.lazy(() => import('@/admin/pages/BillingPage').then((m) => ({ default: m.BillingPage })));
const PricingPage = React.lazy(() => import('@/admin/pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const IntegrationsPage = React.lazy(() => import('@/admin/pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })));
const StoragePage = React.lazy(() => import('@/admin/pages/StoragePage').then((m) => ({ default: m.StoragePage })));
const IngestionHealthPage = React.lazy(() => import('@/admin/pages/IngestionHealthPage').then((m) => ({ default: m.IngestionHealthPage })));
// Customer
const CustomerDashboard = React.lazy(() => import('@/customer/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const CustomerSessionsPage = React.lazy(() => import('@/customer/pages/SessionsPage').then((m) => ({ default: m.SessionsPage })));
const CustomerSessionReplay = React.lazy(() => import('@/customer/pages/SessionReplay').then((m) => ({ default: m.SessionReplay })));
const ApiKeysPage = React.lazy(() => import('@/customer/pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const DataSourcesPage = React.lazy(() => import('@/customer/pages/DataSourcesPage').then((m) => ({ default: m.DataSourcesPage })));
const ChatClientTest = React.lazy(() => import('@/customer/pages/ChatClientTest').then((m) => ({ default: m.ChatClientTest })));
const HandoverConfig = React.lazy(() => import('@/customer/pages/HandoverConfig').then((m) => ({ default: m.HandoverConfig })));
const ChatClientEmbed = React.lazy(() => import('@/customer/pages/ChatClientEmbed').then((m) => ({ default: m.ChatClientEmbed })));
// Config-Sub-Pages
const ConfigLayout = React.lazy(() => import('@/customer/pages/config/ConfigLayout').then((m) => ({ default: m.ConfigLayout })));
const ModelPromptPage = React.lazy(() => import('@/customer/pages/config/ModelPromptPage').then((m) => ({ default: m.ModelPromptPage })));
const WidgetPage = React.lazy(() => import('@/customer/pages/config/WidgetPage').then((m) => ({ default: m.WidgetPage })));
const SecurityPage = React.lazy(() => import('@/customer/pages/config/SecurityPage').then((m) => ({ default: m.SecurityPage })));
const LimitsPage = React.lazy(() => import('@/customer/pages/config/LimitsPage').then((m) => ({ default: m.LimitsPage })));
const AdvancedConfigPage = React.lazy(() => import('@/customer/pages/config/AdvancedConfigPage').then((m) => ({ default: m.AdvancedConfigPage })));
const SpendReportPage = React.lazy(() => import('@/customer/pages/SpendReportPage').then((m) => ({ default: m.SpendReportPage })));
const CreditsPage = React.lazy(() => import('@/customer/pages/CreditsPage').then((m) => ({ default: m.CreditsPage })));
const TopupReturnPage = React.lazy(() => import('@/customer/pages/TopupReturnPage').then((m) => ({ default: m.TopupReturnPage })));
const ChunkCleanupPage = React.lazy(() => import('@/customer/pages/ChunkCleanupPage').then((m) => ({ default: m.ChunkCleanupPage })));
const ChatOptimizationPage = React.lazy(() => import('@/customer/pages/ChatOptimizationPage').then((m) => ({ default: m.ChatOptimizationPage })));
const SkillsPage = React.lazy(() => import('@/customer/pages/SkillsPage').then((m) => ({ default: m.SkillsPage })));
const CustomerAlertsPage = React.lazy(() => import('@/customer/pages/AlertsPage').then((m) => ({ default: m.AlertsPage })));

dayjs.locale('de');

function PageSuspense({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Suspense
      fallback={
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
          <Spin size='large' />
        </div>
      }
    >
      <div className='rag-page'>{children}</div>
    </Suspense>
  );
}

// ── Redirect basierend auf Rolle ─────────────────────

function RoleBasedRedirect(): React.ReactElement {
  const superAdmin = isSuperAdmin();
  return <Navigate to={superAdmin ? '/admin' : '/dashboard'} replace />;
}

function CustomerRouteGuard(): React.ReactElement {
  const location = useLocation();

  if (isSuperAdmin()) {
    // Aktive, noch gültige Impersonation schützen: kein Redirect zum Admin-Bereich,
    // solange der Impersonation-Access-Token im sessionStorage gültig ist.
    const original = impersonationStorage.peek();
    if (original && isImpersonationValid(original)) {
      return <CustomerLayout />;
    }
    impersonationStorage.clear();
    return <Navigate to='/admin' replace state={{ from: location }} />;
  }

  return <CustomerLayout />;
}

export default function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <ConfigProvider
        locale={deDE}
        theme={{
          ...RefineThemes.Blue,
          token: {
            ...(RefineThemes.Blue.token ?? {}),
            borderRadius: 10,
            borderRadiusLG: 14,
            colorBgLayout: '#f5f7fb',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          },
          components: {
            ...(RefineThemes.Blue.components ?? {}),
            Card: { borderRadiusLG: 12 },
            Button: { borderRadius: 8, fontWeight: 500 },
            Table: { headerBg: 'rgba(15, 23, 42, 0.025)', headerSplitColor: 'transparent' },
            Menu: { itemSelectedBg: 'rgba(99, 102, 241, 0.18)', itemHoverBg: 'rgba(99,102,241,0.08)' },
            Breadcrumb: { itemColor: 'rgba(15,23,42,0.55)', linkColor: 'rgba(15,23,42,0.65)', lastItemColor: 'rgba(15,23,42,0.9)', separatorColor: 'rgba(15,23,42,0.35)' },
          },
        }}
      >
        <AntApp>
          <Refine
            routerProvider={routerProvider}
            authProvider={authProvider}
            dataProvider={dataProviderInstance}
            notificationProvider={useNotificationProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
            }}
            resources={[
              // ── Admin-Ressourcen ──────────────────
              {
                name: 'admin/tenants',
                list: '/admin/tenants',
                show: '/admin/tenants/:id',
                meta: {
                  label: 'Tenants',
                  icon: <TeamOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/sessions',
                list: '/admin/sessions',
                show: '/admin/sessions/:id/replay',
                meta: {
                  label: 'Sessions',
                  icon: <MessageOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/alerts',
                list: '/admin/alerts',
                meta: {
                  label: 'Alerts',
                  icon: <BellOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/analytics',
                list: '/admin/analytics',
                meta: {
                  label: 'Analytics',
                  icon: <BarChartOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/billing',
                list: '/admin/billing',
                meta: {
                  label: 'Billing',
                  icon: <CreditCardOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/pricing',
                list: '/admin/pricing',
                meta: {
                  label: 'Preise',
                  icon: <DollarOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/integrations',
                list: '/admin/integrations',
                meta: {
                  label: 'Integrationen',
                  icon: <ApiOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/storage',
                list: '/admin/storage',
                meta: {
                  label: 'Speicher',
                  icon: <HddOutlined />,
                  parent: 'admin',
                },
              },
              {
                name: 'admin/ingestion-health',
                list: '/admin/ingestion-health',
                meta: {
                  label: 'Ingestion-Health',
                  icon: <HeartOutlined />,
                  parent: 'admin',
                },
              },
              // ── Kunden-Ressourcen ─────────────────
              {
                name: 'portal/sessions',
                list: '/dashboard/sessions',
                show: '/dashboard/sessions/:id',
                meta: {
                  label: 'Sessions',
                  icon: <MessageOutlined />,
                  parent: 'dashboard',
                },
              },
              {
                name: 'portal/api-keys',
                list: '/dashboard/api-keys',
                meta: {
                  label: 'API-Keys',
                  icon: <KeyOutlined />,
                  parent: 'dashboard',
                },
              },
              {
                name: 'portal/datasources',
                list: '/dashboard/datasources',
                meta: {
                  label: 'Datenquellen',
                  icon: <DatabaseOutlined />,
                  parent: 'dashboard',
                },
              },
              {
                name: 'portal/config',
                list: '/dashboard/config',
                meta: {
                  label: 'Einstellungen',
                  icon: <SettingOutlined />,
                  parent: 'dashboard',
                },
              },
              {
                name: 'portal/credits',
                list: '/dashboard/credits',
                meta: {
                  label: 'Guthaben',
                  icon: <WalletOutlined />,
                  parent: 'dashboard',
                },
              },
              {
                name: 'portal/alerts',
                list: '/dashboard/alerts',
                meta: {
                  label: 'Alerts',
                  icon: <BellOutlined />,
                  parent: 'dashboard',
                },
              },
            ]}
          >
            <Routes>
              {/* ── Login ──────────────────────────── */}
              <Route path='/login' element={<LoginPage />} />
              <Route path='/forgot-password' element={<ForgotPasswordPage />} />
              <Route path='/reset-password' element={<ResetPasswordPage />} />

              {/* ── Authenticated Root ─────────────── */}
              <Route
                element={
                  <Authenticated key='auth-check' fallback={<Navigate to='/login' />}>
                    <Outlet />
                  </Authenticated>
                }
              >
                {/* Root: Redirect basierend auf Rolle */}
                <Route index element={<RoleBasedRedirect />} />

                {/* ── Admin-Bereich ──────────────── */}
                <Route path='/admin' element={<AdminLayout />}>
                  <Route index element={<Navigate to='/admin/dashboard' replace />} />
                  <Route
                    path='dashboard'
                    element={
                      <PageSuspense>
                        <AdminDashboard />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='tenants'
                    element={
                      <PageSuspense>
                        <TenantsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='tenants/:id'
                    element={
                      <PageSuspense>
                        <TenantDetail />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='sessions'
                    element={
                      <PageSuspense>
                        <AdminSessionsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='sessions/:id/replay'
                    element={
                      <PageSuspense>
                        <AdminSessionReplay />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='alerts'
                    element={
                      <PageSuspense>
                        <AlertsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='analytics'
                    element={
                      <PageSuspense>
                        <AnalyticsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='billing'
                    element={
                      <PageSuspense>
                        <BillingPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='pricing'
                    element={
                      <PageSuspense>
                        <PricingPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='integrations'
                    element={
                      <PageSuspense>
                        <IntegrationsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='storage'
                    element={
                      <PageSuspense>
                        <StoragePage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='ingestion-health'
                    element={
                      <PageSuspense>
                        <IngestionHealthPage />
                      </PageSuspense>
                    }
                  />
                </Route>

                {/* ── Kunden-Dashboard ───────────── */}
                <Route path='/dashboard' element={<CustomerRouteGuard />}>
                  <Route index element={<Navigate to='/dashboard/overview' replace />} />
                  <Route
                    path='overview'
                    element={
                      <PageSuspense>
                        <CustomerDashboard />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='sessions'
                    element={
                      <PageSuspense>
                        <CustomerSessionsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='sessions/:id'
                    element={
                      <PageSuspense>
                        <CustomerSessionReplay />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='api-keys'
                    element={
                      <PageSuspense>
                        <ApiKeysPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='datasources'
                    element={
                      <PageSuspense>
                        <DataSourcesPage />
                      </PageSuspense>
                    }
                  />
                  {/* Config-Bereich mit Sub-Navigation */}
                  <Route
                    path='config'
                    element={
                      <PageSuspense>
                        <ConfigLayout />
                      </PageSuspense>
                    }
                  >
                    <Route index element={<Navigate to='model' replace />} />
                    <Route path='ai' element={<Navigate to='/dashboard/config/model' replace />} />
                    <Route path='model' element={<PageSuspense><ModelPromptPage /></PageSuspense>} />
                    <Route path='widget' element={<PageSuspense><WidgetPage /></PageSuspense>} />
                    <Route path='handover' element={<PageSuspense><HandoverConfig /></PageSuspense>} />
                    <Route path='security' element={<PageSuspense><SecurityPage /></PageSuspense>} />
                    <Route path='limits' element={<PageSuspense><LimitsPage /></PageSuspense>} />
                    <Route path='advanced' element={<PageSuspense><AdvancedConfigPage /></PageSuspense>} />
                    <Route path='embed' element={<PageSuspense><ChatClientEmbed /></PageSuspense>} />
                  </Route>
                  {/* Alte Routen → Redirect auf Config-Sub-Pages */}
                  <Route path='handover' element={<Navigate to='/dashboard/config/handover' replace />} />
                  <Route path='embed' element={<Navigate to='/dashboard/config/embed' replace />} />
                  <Route
                    path='chat-test'
                    element={
                      <PageSuspense>
                        <ChatClientTest />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='chat-optimization'
                    element={
                      <PageSuspense>
                        <ChatOptimizationPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='spend-report'
                    element={
                      <PageSuspense>
                        <SpendReportPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='credits'
                    element={
                      <PageSuspense>
                        <CreditsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='credits/topups/:topupId/return'
                    element={
                      <PageSuspense>
                        <TopupReturnPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='chunk-cleanup'
                    element={
                      <PageSuspense>
                        <ChunkCleanupPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='skills'
                    element={
                      <PageSuspense>
                        <SkillsPage />
                      </PageSuspense>
                    }
                  />
                  <Route
                    path='alerts'
                    element={
                      <PageSuspense>
                        <CustomerAlertsPage />
                      </PageSuspense>
                    }
                  />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
        </AntApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}
